/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react';
import { LoaderOverlay } from 'components/LoaderOverlay/LoaderOverlay';
import { useCartContext } from 'context/CartContext/CartContext';
import { GeneralConcertInfo } from './ConcertInfo/ConcertInfo';
import { TicketCart } from 'pages/EventPage/TicketCart/TicketCart';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from 'react-auth-kit';
import { ROUTES } from 'router/routes';
import { ModalLogin } from 'components/ModalLogin/ModalLogin';
import { getInvitation } from 'api/invitations';
import { useTimeslotSelection } from 'hooks/useTimeslotSelection';
import axios from 'axios';

import { Hall } from './Hall/Hall';
import { TimeslotSelector } from 'pages/EventPage/TimeslotSelector/TimeslotSelector';
import { useConcertQuery } from './hooks/useConcert';
import { GoodToKnowCard } from 'components/GoodToKnow/GoodToKnowCard';
import { FormattedMessage } from 'react-intl';
import { useEventTracking } from 'hooks/useEventTracking';

export const SingleConcertPage = () => {
  const { eventTracking } = useEventTracking();
  const navigate = useNavigate();
  useEffect(() => {
    eventTracking.page.viewed({
      pageName: 'concert_page',
      url: window.location.href,
    });
  }, [eventTracking]);
  const { eventKey } = useParams();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitationId') ?? undefined;
  const isAuthenticated = useIsAuthenticated();
  const authenticated = isAuthenticated();

  const queryEnabled = !invitationId || authenticated;
  const { data, isFetching, isError } = useConcertQuery(eventKey, invitationId, queryEnabled);
  const { tickets, setEventId, setEventKey, setInvitationId, clearCart } = useCartContext();

  // Clear stale cart on every page mount (handles refresh + back-from-checkout).
  // Tickets from a previous session have deselect:noop and cannot be removed otherwise.
  useEffect(() => {
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isValidSelection, setIsValidSelection] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationValidated, setInvitationValidated] = useState(false);
  const [pendingCloseCheck, setPendingCloseCheck] = useState(false);
  // Keyed on the event so the slot selection resets if the event changes (even
  // without a remount), preventing one event's slot from leaking to another.
  const { selectedSlotEventKey, selectedSlotPricing, handleSlotSelect } = useTimeslotSelection(
    data?.eventKey,
  );

  const hasTimeslots = Boolean(data?.timeslots && data.timeslots.length > 0);
  const effectiveEventKey =
    hasTimeslots && selectedSlotEventKey ? selectedSlotEventKey : data?.eventKey ?? '';
  const effectivePricing =
    hasTimeslots && selectedSlotPricing ? selectedSlotPricing : data?.pricing ?? [];

  useEffect(() => {
    if (data && !isFetching) {
      if (!data.timeslots || data.timeslots.length === 0) {
        setEventId(data.eventId);
        setEventKey(data.eventKey);
      }
      if (invitationId) setInvitationId(invitationId);

      eventTracking.event.viewed({
        eventId: data.eventId,
        eventName: data.title || data.eventKey,
        eventDate: data.date,
        venue: data.stadium!,
      });
    }
  }, [data?.eventId, data?.id]);

  useEffect(() => {
    setInvitationValidated(false);
    setInvitationError(null);
  }, [invitationId]);

  useEffect(() => {
    if (!invitationId || !authenticated) return;
    setInvitationId(invitationId);
    getInvitation(invitationId)
      .then(() => setInvitationValidated(true))
      .catch((e) => {
        const status = axios.isAxiosError(e) ? e.response?.status : undefined;
        setInvitationError(
          status === 403 ? 'هذا الرقم غير مدعو لهذا الحدث' : 'حدث خطأ في التحقق من الدعوة',
        );
      });
  }, [invitationId, authenticated]);

  const handleOpenModal = useCallback(() => {
    eventTracking.login.clicked({});
    setIsAuthModalOpen(true);
  }, [eventTracking]);

  // Close modal when user becomes authenticated
  useEffect(() => {
    if (authenticated && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [authenticated, isAuthModalOpen]);

  // After the invitation modal closes, check auth in a useEffect (runs after React
  // has flushed all state including signIn()), so we don't navigate home on a
  // successful login that closed the modal.
  useEffect(() => {
    if (!pendingCloseCheck) return;
    setPendingCloseCheck(false);
    if (!authenticated) navigate(ROUTES.home);
  }, [pendingCloseCheck, authenticated, navigate]);

  // While waiting for auth on invitation events, show the login modal.
  if (!queryEnabled) {
    return <ModalLogin isOpen onClose={() => setPendingCloseCheck(true)} />;
  }

  if (isFetching) {
    return <LoaderOverlay />;
  }

  if (!data || isError) return null;

  const isInvitationOnly = data.isInvitationBased;
  const needsInvitationValidation =
    isInvitationOnly &&
    invitationId &&
    isAuthenticated() &&
    !invitationValidated &&
    !invitationError;

  const renderSeatingArea = () => {
    if (isInvitationOnly && !invitationId) {
      return (
        <Box p={8} textAlign='center'>
          <Text textStyle='headlineMedium' color='dark.D900'>
            <FormattedMessage
              id='invitation.error.inviteOnly'
              defaultMessage='هذا الحدث بالدعوة فقط'
            />
          </Text>
        </Box>
      );
    }
    if (invitationError) {
      return (
        <Box p={8} textAlign='center'>
          <Text textStyle='headlineMedium' color='red'>
            {invitationError}
          </Text>
        </Box>
      );
    }
    if (needsInvitationValidation) {
      return <LoaderOverlay />;
    }
    return (
      <Hall
        key={`hall-${selectedSlotEventKey ?? data.eventKey}-${isAuthenticated()}-${
          data.requireAuthentication
        }-${isAuthModalOpen}`}
        pricing={effectivePricing}
        event={effectiveEventKey}
        eventId={data.eventId}
        currency={data.currency}
        maxAllowedTickets={data.maxAllowedTickets}
        setIsValidSelection={setIsValidSelection}
        onOpenModal={handleOpenModal}
        requireAuthentication={data.requireAuthentication}
        disabled={hasTimeslots && !selectedSlotEventKey}
      />
    );
  };

  return (
    <Box dir='rtl'>
      <GeneralConcertInfo {...data} hasTimeslots={hasTimeslots} />

      {data.timeslots && data.timeslots.length > 0 && (
        <TimeslotSelector
          timeslots={data.timeslots}
          selectedEventKey={selectedSlotEventKey}
          onSelect={handleSlotSelect}
        />
      )}

      <Grid mx='auto' maxW='1440px' templateColumns={{ base: '1fr', md: '460px 1fr' }}>
        <GridItem h={{ md: '1000px' }} minH='230px'>
          <TicketCart
            currency={data.currency}
            selectedTickets={tickets}
            isValidSelection={isValidSelection}
            eventId={data.eventId}
            isFreeEvent={data.isFreeEvent}
          />
        </GridItem>
        <GridItem rowStart={{ base: 1, md: 'auto' }} alignSelf='start' h={{ md: '1000px' }}>
          {renderSeatingArea()}
        </GridItem>
      </Grid>

      {data.goodToKnow && data.goodToKnow.length > 0 && (
        <Box>
          <Heading
            my={12}
            textAlign='center'
            textStyle='headingSmall'
            color='dark.D900'
            fontWeight='600'
          >
            <FormattedMessage id='eventpage.goodToKnow' defaultMessage='معلومات مهمة' />
          </Heading>
          <GoodToKnowCard items={data.goodToKnow} />
        </Box>
      )}
      <ModalLogin isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </Box>
  );
};
